import type { InsuranceDocType as InsuranceDocTypePrisma } from '@/backend/src/services/other/prisma.js'
import type { AppConfig } from '@/general/src/appConfig/utils.server.js'
import { insuraceDocAiKnowledge } from '@/general/src/insuranceDoc/knowledge.backend.js'
import { checkEnumEq } from '@/general/src/other/checkEnumEq.js'
import { getConstKeys } from '@/general/src/other/getConstKeys.js'
import { getOneSharedEnv } from '@/general/src/other/sharedEnv.js'
import Papa from 'papaparse'
import { z } from 'zod'

export const insuranceDocTypesMap = {
  passport: 'Паспорт',
  sts: 'СТС',
  pts: 'ПТС',
  epts: 'Электронный ПТС',
  driverLicense: 'Водительское удостоверение',
} as const
export const insuranceDocTypes = getConstKeys(insuranceDocTypesMap)
export const zInsuranceDocType = z.enum(insuranceDocTypes)
export type InsuranceDocType = (typeof insuranceDocTypes)[number]
checkEnumEq<InsuranceDocType, InsuranceDocTypePrisma, true>()

export const insuranceDocParseProvidersMap = {
  beorg: 'Beorg',
  openai: 'OpenAI',
  beorgPlus: 'BeorgPlus',
} as const
export const insuranceDocParseProviders = getConstKeys(insuranceDocParseProvidersMap)
export const zInsuranceDocParseProvider = z.enum(insuranceDocParseProviders)
export type InsuranceDocParseProvider = (typeof insuranceDocParseProviders)[number]

export const getInsuranceDocUploadUrl = (
  insuranceDocUpload: {
    id: string
    ext: string
    s3Key: string
    s3KeyPreview: string
  },
  settings: {
    policy: 'insuranceDocFull' | 'insuranceDocPreview'
  }
) => {
  const s3Key = settings.policy === 'insuranceDocPreview' ? insuranceDocUpload.s3KeyPreview : insuranceDocUpload.s3Key
  return `${getOneSharedEnv('BACKEND_URL')}/api/download/${s3Key}`
}

export const zClientInsuranceDocUpload = z.object({
  id: z.string(),
  originalFileName: z.string(),
  url: z.string(),
})
export const zClientInsuranceDocUploads = z.array(zClientInsuranceDocUpload)

export type InsuranceDocMapItem = {
  label: string
  hint?: string
  docLabel?: string
  aiHint?: string
}

export const getInsuranceDocMap = <TZSchema extends z.ZodObject<any, any>, TExtraKey extends string>({
  csv,
  zSchema,
  extra,
}: {
  csv: string
  zSchema: TZSchema
  extra?: Record<TExtraKey, InsuranceDocMapItem>
}): Record<keyof z.infer<TZSchema> | TExtraKey, InsuranceDocMapItem> => {
  const records = Papa.parse<{
    alias: string
    'Название поля в программе': string
    'Название поля на фотографии в документе': string
    'Подсказка для ИИ': string
  }>(csv, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (header) => header.trim(),
  }).data
  const map: Record<string, InsuranceDocMapItem> = {}
  Object.assign(map, extra)
  for (const record of records) {
    if (!record.alias) continue
    map[record.alias] = {
      label: record['Название поля в программе'] || '',
      docLabel: record['Название поля на фотографии в документе'] || '',
      aiHint: record['Подсказка для ИИ'] || '',
    }
  }
  const neededKeys = Object.keys(zSchema.shape).sort()
  const existingKeys = Object.keys(map).sort()
  if (neededKeys.some((key) => !existingKeys.includes(key))) {
    throw new Error(
      `Несовпадающие ключи в csv+extra и zSchema: ${neededKeys.filter((key) => !existingKeys.includes(key)).join(', ')}`
    )
  }
  if (existingKeys.some((key) => !neededKeys.includes(key))) {
    throw new Error(
      `Несовпадающие ключи в csv+extra и zSchema: ${existingKeys.filter((key) => !neededKeys.includes(key)).join(', ')}`
    )
  }
  return map as never
}

export const getAiPromptByInsuranceDocMapItem = (key: string, item: InsuranceDocMapItem) => {
  const docLabelPrompt = item.docLabel && `(На фотографии документа обычно помечено как "${item.docLabel}")`
  let aiHintPrompt = item.aiHint
  if (aiHintPrompt) {
    for (const [insuraceDocAiKnowledgeKey, insuraceDocAiKnowledgeValue] of Object.entries(insuraceDocAiKnowledge)) {
      aiHintPrompt = aiHintPrompt.replaceAll(`{{${insuraceDocAiKnowledgeKey}}}`, insuraceDocAiKnowledgeValue)
    }
    aiHintPrompt = `(${aiHintPrompt})`
  }
  const promptMerged = [item.label, docLabelPrompt, aiHintPrompt].filter(Boolean).join(' ')
  return `"${key}": ${promptMerged}`
}

export const getAiZSchemaByInsuranceDocMap = (map: Record<string, InsuranceDocMapItem>, omitKeys: string[] = []) => {
  return z.object({
    ...Object.fromEntries(
      Object.entries(map)
        .filter(([key]) => !omitKeys.includes(key))
        .map(([key, item]) => {
          if (key === 'gender') {
            return [key, z.enum(['male', 'female'])]
          }
          return [key, z.string()]
        })
    ),
  })
}

export const getAiPromptByInsuranceDocMap = (map: Record<string, InsuranceDocMapItem>) => {
  return Object.entries(map)
    .map(([key, item]) => getAiPromptByInsuranceDocMapItem(key, item))
    .join('\n')
}

export const getRecognitionTokenCostByMap = ({
  recognitionTokenCostMap,
  provider,
  insuranceDocType,
}: {
  recognitionTokenCostMap: AppConfig['recognitionTokenCostMap']
  provider: 'openai' | 'beorg' | 'beorgPlus'
  insuranceDocType: InsuranceDocType
}) => {
  switch (provider) {
    case 'openai':
      switch (insuranceDocType) {
        case 'sts':
          return recognitionTokenCostMap.openai.parseSts
        case 'pts':
          return recognitionTokenCostMap.openai.parsePts
        case 'epts':
          return recognitionTokenCostMap.openai.parseEpts
        case 'driverLicense':
          return recognitionTokenCostMap.openai.parseDriverLicense
        case 'passport':
          return recognitionTokenCostMap.openai.parsePassport
        default:
          return 999
      }
    case 'beorg':
      switch (insuranceDocType) {
        case 'sts':
          return recognitionTokenCostMap.beorg.parseSts
        case 'pts':
          return recognitionTokenCostMap.beorg.parsePts
        case 'epts':
          return recognitionTokenCostMap.beorg.parseEpts
        case 'driverLicense':
          return recognitionTokenCostMap.beorg.parseDriverLicense
        case 'passport':
          return recognitionTokenCostMap.beorg.parsePassport
        default:
          return 999
      }
    case 'beorgPlus':
      switch (insuranceDocType) {
        case 'sts':
          return recognitionTokenCostMap.beorg.parseStsPlus
        case 'pts':
          return recognitionTokenCostMap.beorg.parsePtsPlus
        case 'epts':
          return recognitionTokenCostMap.beorg.parseEptsPlus
        case 'driverLicense':
          return recognitionTokenCostMap.beorg.parseDriverLicensePlus
        case 'passport':
          return recognitionTokenCostMap.beorg.parsePassportPlus
        default:
          return 999
      }
    default:
      return 999
  }
}
